<script lang="ts">
  /**
   * A settings row that turns something on or off: an optional badge and a
   * label (with an optional line of detail) on the left, a `Switch` on the
   * right. It lives inside a `Group`. TwinTen's settings toggle row.
   *
   * The detail line describes the switch (`aria-describedby`), so a screen
   * reader hears what the setting does along with its name and state.
   *
   * `children` is a second, full-width line inside the same row, under the
   * label: the control that belongs to the thing switched on (a volume
   * slider under a sound toggle; see `SliderRow`'s `bare`).
   *
   * Test id: `testid` is the switch's (what a test presses).
   */
  import type { Snippet } from 'svelte';

  import Switch from './Switch.svelte';

  interface Props {
    /** The visible label, and the switch's accessible name. */
    readonly label: string;
    readonly detail?: string | undefined;
    readonly checked: boolean;
    /** Called with the value a press asks for (the opposite of `checked`). */
    readonly onchange: (checked: boolean) => void;
    readonly disabled?: boolean;
    /** What leads the row: a `RowBadge`. */
    readonly badge?: Snippet | undefined;
    readonly children?: Snippet | undefined;
    /** The test id of the switch itself. */
    readonly testid?: string | undefined;
  }
  const {
    label,
    detail,
    checked,
    onchange,
    disabled = false,
    badge,
    children,
    testid,
  }: Props = $props();

  const detailId = $props.id();
</script>

<div class="ui-toggle-row">
  <span class="ui-toggle-row-lead">
    {#if badge !== undefined}{@render badge()}{/if}
    <span class="ui-toggle-row-text">
      <span>{label}</span>
      {#if detail !== undefined}
        <span class="ui-toggle-row-detail" id={detailId}>{detail}</span>
      {/if}
    </span>
  </span>
  <Switch
    {label}
    {checked}
    {onchange}
    {disabled}
    {testid}
    describedby={detail !== undefined ? detailId : undefined}
  />
  {#if children !== undefined}
    <div class="ui-toggle-row-below">{@render children()}</div>
  {/if}
</div>

<style>
  .ui-toggle-row {
    box-sizing: border-box;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    row-gap: var(--ui-space-2);
    padding: 0.6rem 1rem;
    border-bottom: 1px solid var(--ui-line);
    color: var(--ui-ink);
    font: var(--ui-text-body);
  }

  .ui-toggle-row:last-child {
    border-bottom: 0;
  }

  .ui-toggle-row-lead {
    flex: 1 1 0;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    min-width: 0;
    margin-right: 0.75rem;
  }

  .ui-toggle-row-text {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    min-width: 0;
  }

  .ui-toggle-row-detail {
    font: var(--ui-text-caption);
    font-weight: 400;
    line-height: 1.35;
    color: var(--ui-ink-2);
  }

  .ui-toggle-row-below {
    flex: 1 0 100%;
    display: flex;
    align-items: center;
    padding-bottom: 0.25rem;
  }
</style>
